import "./CurrentBillPeriodSection.scss";

import { ReactComponent as ForwardIcon } from "@flick/front_end_patterns/src/images/icon_nav_arrow_forward.svg";
import { useQuery } from "@tanstack/react-query";
import { CurrentBillSpendUnavailable } from "components/CurrentBillSpendUnavailable";
import { LastPeriodComparison } from "components/LastPeriodComparison";
import {
  add,
  differenceInDays,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
} from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { getAggregates } from "queries/rating";
import React from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "reduxUtils/hook";
import {
  getAggregatesTotalValue,
  getValidAggregateTotals,
} from "utils/aggregateCalculations/findValidAggregateTotals";
import { AVAILABLE_PERIODS, PERIOD_CODES } from "utils/constants";
import { NZD } from "utils/currency";
import { jwtClient } from "utils/jwtClient";

import { withErrorBoundary } from "../fallback/withErrorBoundary";
import { LoadingSection } from "../LoadingSection";

const DELAY_FOR_BILLING_PERIOD_DATA = 2;

const ratingQueryDateRange = (
  billingEntityData: BillingEntity,
): { endAt?: Date; startAt?: Date } => {
  if (!billingEntityData) {
    return {
      startAt: undefined,
      endAt: undefined,
    };
  }
  const nextInvoiceDates = billingEntityData.next_invoice_dates;
  const nextInvoiceFrom = new Date(nextInvoiceDates.invoice_from);
  const invoiceUntil = new Date(nextInvoiceDates.invoice_until);

  if (doesNextInvoiceIncludeCurrentConsumption(invoiceUntil)) {
    return {
      startAt: nextInvoiceFrom,
      endAt: invoiceUntil,
    };
  } else if (isCurrentConsumptionDataUnavailable(invoiceUntil)) {
    return {
      startAt: nextInvoiceFrom,
      endAt: invoiceUntil,
    };
  } else {
    return {
      // Hack. add 1 second here to fix a bug when one billing cycle has just ended and another has begun
      startAt: add(invoiceUntil, { seconds: 1 }),
      endAt: calculatePeriodEnd(
        invoiceUntil,
        billingEntityData.nominated_billing_period,
      ),
    };
  }
};

const calculatePeriodEnd = (nextInvoiceUntil: Date, period: string): Date => {
  const startAt = add(nextInvoiceUntil, { seconds: 1 });
  switch (period) {
    case PERIOD_CODES.P1M:
      return endOfMonth(startAt);
    case PERIOD_CODES.P2W:
      return add(endOfWeek(startAt, { weekStartsOn: 1 }), { weeks: 1 });
    default:
      return endOfWeek(startAt, { weekStartsOn: 1 });
  }
};

const doesNextInvoiceIncludeCurrentConsumption = (
  invoiceUntil: Date,
): boolean => {
  return isBefore(new Date(), invoiceUntil);
};

const isCurrentConsumptionDataUnavailable = (invoiceUntil: Date): boolean => {
  return isBefore(
    new Date(),
    add(invoiceUntil, {
      days: DELAY_FOR_BILLING_PERIOD_DATA,
    }),
  );
};

/**
 * @description CurrentBillPeriodSection component
 * used in HomePage to display current bill
 */
export const CurrentBillPeriodSection = withErrorBoundary(() => {
  const { supplyNodeRef } = useAppSelector((store) => store.currentAccount);
  const { billingEntityData } = useAppSelector(
    (store) => store.billingEntityData,
  );
  const {
    data: aggregatesData,
    isFetching: isAggregatesStatusFetching,
    isSuccess: isAggregatesSuccess,
  } = useQuery<Aggregates, Error>(
    [
      "aggregates",
      {
        supplyNodeRef,
        startAt: ratingQueryDateRange(billingEntityData).startAt,
        endAt: ratingQueryDateRange(billingEntityData).endAt,
        jwtClient,
      },
    ],
    getAggregates,
    {
      enabled: Boolean(billingEntityData) && Boolean(supplyNodeRef),
      useErrorBoundary: true,
    },
  );

  const { periodName, duration } =
    AVAILABLE_PERIODS[billingEntityData.nominated_billing_period];

  const formattedPeriodEndDate = format(
    utcToZonedTime(
      ratingQueryDateRange(billingEntityData).endAt,
      "Pacific/Auckland",
    ),
    "EEE d MMM",
  );

  // if todays date is before the end of the current invoice period then return the number of days until it ends, otherwise the next invoice period isnt ready to start yet so return 0
  const daysRemaining = isBefore(
    new Date(),
    ratingQueryDateRange(billingEntityData).endAt,
  )
    ? differenceInDays(
        ratingQueryDateRange(billingEntityData).endAt,
        new Date(),
      )
    : 0;

  const progressPercentage = ((duration - daysRemaining) / duration) * 100;

  if (isAggregatesStatusFetching) {
    return <LoadingSection />;
  }

  const validAggregateTotals = getValidAggregateTotals(aggregatesData);

  const aggregatesTotalValue = getAggregatesTotalValue(validAggregateTotals);

  if (
    (!aggregatesData && isAggregatesSuccess) ||
    (aggregatesData &&
      isAggregatesSuccess &&
      Array.isArray(!aggregatesData.aggregates)) ||
    !aggregatesTotalValue
  ) {
    return (
      <CurrentBillSpendUnavailable
        periodName={periodName}
        periodEnd={formattedPeriodEndDate}
        daysRemaining={daysRemaining}
        progressPercentage={progressPercentage}
      />
    );
  }

  // covert Dates to numbers by getTime() https://stackoverflow.com/a/70739006
  const allValidDates = validAggregateTotals.map((validTotal) =>
    new Date(validTotal.end_at).getTime(),
  );

  const latestValidDate =
    allValidDates.length > 0
      ? new Date(Math.max(...allValidDates))
      : new Date(aggregatesData.aggregates[0].end_at);

  let daysPassed =
    aggregatesData.aggregates.findIndex((aggregate) =>
      isSameDay(new Date(aggregate.end_at), latestValidDate),
    ) + 1;
  if (daysPassed === 0) {
    daysPassed = 1;
  }

  return (
    <>
      <p>
        For the {periodName} ending {formattedPeriodEndDate}
      </p>
      <p className="h1">
        {aggregatesTotalValue && NZD.format(aggregatesTotalValue)}
      </p>
      {aggregatesTotalValue && daysPassed > 0 && billingEntityData && (
        <LastPeriodComparison
          thisPeriodValue={aggregatesTotalValue}
          thisPeriodStartAt={ratingQueryDateRange(billingEntityData)?.startAt}
          daysThroughPeriod={daysPassed}
          nominatedBillingPeriod={billingEntityData.nominated_billing_period}
          componentTitle="Last period comparison"
        />
      )}
      <div className="progress">
        <div
          className="progress__bar"
          style={{ width: `${progressPercentage}%` }}
        >
          {" "}
          {daysRemaining} days to go
        </div>
      </div>
      <small className="latest-valid-date">
        as at{" "}
        {format(
          utcToZonedTime(latestValidDate, "Pacific/Auckland"),
          "EEE d MMM",
        )}
      </small>
      {periodName && (
        <section className="customer_tools_section">
          <article className="customer_tools_section__link_container bill_period_usage">
            <Link
              to={`/usage/${billingEntityData.nominated_billing_period}`}
              className="customer_tools_section__link bill_period_usage__link bill_period_usage__link--with-icon"
              state={{ previousPath: window.location.pathname }}
            >
              <strong>Your usage this {periodName}</strong>
              <ForwardIcon />
            </Link>
          </article>
        </section>
      )}
    </>
  );
});
